import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase, ensureSupabaseReady } from '../lib/supabase';
import toast from 'react-hot-toast';
import GoogleButton from './GoogleButton';
import { Eye, EyeOff } from 'lucide-react';

interface AuthModalProps {
  show: boolean;
  onClose: () => void;
  tenantLogoUrl?: string;
  isTenant?: boolean;
  tenantOrganizerName?: string;
}

export default function AuthModal({ show, onClose, tenantLogoUrl, isTenant, tenantOrganizerName }: AuthModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showBrandAlt, setShowBrandAlt] = useState(true);
  useEffect(() => {
    if (!isTenant) return;
    const id = setInterval(() => setShowBrandAlt(v => !v), 4000);
    return () => clearInterval(id);
  }, [isTenant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const ready = await ensureSupabaseReady();
      if (!ready.ok) {
        toast.error('Unable to reach authentication service. Please check your network and configuration.');
        return;
      }
      if (isPasswordReset) {
        const { error } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (error) {
          toast.error(error.message);
        } else {
          toast.success('Password updated successfully');
          setIsPasswordReset(false);
          onClose();
        }
      } else if (isSignUp) {
        try {
          const { data: exists } = await supabase.rpc('user_exists_by_email', { target_email: email });
          if (exists === true) {
            toast.error('An account already exists for this email. Please sign in.');
            setIsSignUp(false);
            return;
          }
        } catch { }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) {
          const msg = (error.message || '').toLowerCase();
          if (msg.includes('already registered') || msg.includes('already') || msg.includes('exists')) {
            toast.error('An account already exists for this email. Please sign in.');
            setIsSignUp(false);
          } else {
            toast.error(error.message);
          }
        } else if (data.user) {
          toast.success('User signed up. Please check your email to confirm your account.');
          setIsSignUp(false);
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            try {
              const { data: exists } = await supabase.rpc('user_exists_by_email', { target_email: email });
              if (exists === false) {
                toast.error('No account found for this email. Please sign up.');
              } else {
                toast.error('Invalid email or password');
              }
            } catch {
              toast.error('Invalid email or password');
            }
          } else if (error.message.includes('Email not confirmed')) {
            toast.error('Please confirm your email before signing in');
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success('Successfully logged in');
          onClose();
        }
      }
    } catch (error: any) {
      const msg = typeof error?.message === 'string' ? error.message : '';
      if (/Failed to fetch|NetworkError|TypeError/i.test(msg)) {
        toast.error('Network error while contacting authentication service');
      } else {
        toast.error(msg || 'An error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    console.log('Google Sign-In Clicked!');
    try {
      const ready = await ensureSupabaseReady();
      if (!ready.ok) {
        toast.error('Unable to reach authentication service. Please check your network and configuration.');
        return;
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;
      onClose();
    } catch (error: any) {
      const msg = typeof error?.message === 'string' ? error.message : '';
      if (/Failed to fetch|NetworkError|TypeError/i.test(msg)) {
        toast.error('Network error while contacting authentication service');
      } else {
        toast.error('Failed to sign in with Google. Please try again.');
      }
      console.error('Google sign-in error:', error);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      const ready = await ensureSupabaseReady();
      if (!ready.ok) {
        toast.error('Unable to reach authentication service. Please check your network and configuration.');
        return;
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Password reset instructions sent to your email');
      }
    } catch (error: any) {
      const msg = typeof error?.message === 'string' ? error.message : '';
      if (/Failed to fetch|NetworkError|TypeError/i.test(msg)) {
        toast.error('Network error while contacting authentication service');
      } else {
        toast.error('Failed to send reset instructions');
      }
    }
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg w-full max-w-[95vw] sm:max-w-md md:max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none"
          >
            ✕
          </button>
        </div>
        {isTenant && (tenantLogoUrl || tenantOrganizerName) ? (
          <div className="sm:hidden brand-fade-container mx-auto mb-4">
            <h1 className={`text-2xl font-bold urbanist-brand bg-gradient-to-r from-black to-gray-700 dark:bg-gradient-to-r dark:from-white dark:to-gray-500 bg-clip-text text-transparent absolute inset-0 flex items-center justify-center transition-opacity duration-700 ${showBrandAlt ? 'opacity-100' : 'opacity-0'}`}>Eventador</h1>
            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-700 ${showBrandAlt ? 'opacity-0' : 'opacity-100'}`}>
              {tenantLogoUrl ? (
                <img src={tenantLogoUrl} alt="Organizer" className="h-12 w-auto" />
              ) : (
                <h1 className="text-2xl font-bold urbanist-brand bg-gradient-to-r from-black to-gray-700 dark:bg-gradient-to-r dark:from-white dark:to-gray-500 bg-clip-text text-transparent">{tenantOrganizerName}</h1>
              )}
            </div>
          </div>
        ) : null}
        <div className="hidden sm:flex justify-center mb-4 items-center gap-2">
          <h1 className="text-2xl font-bold urbanist-brand bg-gradient-to-r from-black to-gray-700 dark:bg-gradient-to-r dark:from-white dark:to-gray-500 bg-clip-text text-transparent">Eventador</h1>
          {isTenant ? (
            <>
              <span className="text-gray-700 dark:text-gray-300 font-bold">×</span>
              {tenantLogoUrl ? (
                <img src={tenantLogoUrl} alt="Organizer" className="h-8 w-auto" />
              ) : tenantOrganizerName ? (
                <span className="text-2xl font-bold urbanist-brand bg-gradient-to-r from-black to-gray-700 dark:bg-gradient-to-r dark:from-white dark:to-gray-500 bg-clip-text text-transparent">{tenantOrganizerName}</span>
              ) : null}
            </>
          ) : null}
        </div>
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
          {isPasswordReset ? 'Update Password' : isSignUp ? 'Create Account' : 'Sign In'}
        </h2>

        {!isPasswordReset && (
          <GoogleButton onClick={handleGoogleSignIn} />
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isPasswordReset && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-black dark:text-white"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {isPasswordReset ? 'New Password' : 'Password'}
            </label>
            <div className="relative">
              <input
                type={passwordVisible ? 'text' : 'password'}
                value={isPasswordReset ? newPassword : password}
                onChange={(e) => isPasswordReset ? setNewPassword(e.target.value) : setPassword(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-black dark:text-white"
                required
              />
              <button
                type="button"
                onClick={() => setPasswordVisible(v => !v)}
                className="absolute inset-y-0 right-2 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                aria-label={passwordVisible ? 'Hide password' : 'Show password'}
              >
                {passwordVisible ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 rounded-md bg-gray-800 text-white hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Loading...' : (isPasswordReset ? 'Update Password' : isSignUp ? 'Sign Up' : 'Sign In')}
          </button>

          {!isPasswordReset && (
            <>
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setEmail('');
                  setPassword('');
                }}
                className="w-full text-indigo-600 hover:text-indigo-800 text-sm"
              >
                {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
              </button>

              {!isSignUp && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="w-full text-gray-600 hover:text-gray-800 text-sm"
                >
                  Forgot Password?
                </button>
              )}
            </>
          )}
        </form>
      </div>
    </div>,
    document.body
  );
}
